import React, { useState, useEffect } from 'react';
import { triggerNotificationPrompt } from '../components/NotificationOnboarding';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  CheckCircle2, Clock, AlertCircle, Calendar, 
  FileText, Award, TrendingUp, Timer, ClipboardList,
  Users, LogIn
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { tasksAPI, reportsAPI, timesheetsAPI } from '../services/api';
import apiClient from '../services/api';
import { formatDateInTimezone, getUserTimezone } from '../utils/timezone';

const VADashboard = () => {
  const [stats, setStats] = useState(null);
  const [myTasks, setMyTasks] = useState([]);
  const [todayHours, setTodayHours] = useState(0);
  const [clientBreakup, setClientBreakup] = useState([]);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [myClients, setMyClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchDashboardData();
    triggerNotificationPrompt();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [tasksRes, timesheetsRes, attendanceRes] = await Promise.all([
        tasksAPI.getAll(),
        timesheetsAPI.getAll(),
        apiClient.get('/attendance/today').catch(() => ({ data: null }))
      ]);

      // Fetch assigned clients
      try {
        const clientsRes = await apiClient.get('/team/my-clients');
        const clientsData = clientsRes.data;
        setMyClients(Array.isArray(clientsData) ? clientsData : (clientsData?.clients || []));
      } catch (_) {}

      // Calculate stats from tasks
      const allTasks = tasksRes.data || [];
      const pending = allTasks.filter(t => t.status === 'pending').length;
      const inProgress = allTasks.filter(t => t.status === 'in_progress').length;
      const completed = allTasks.filter(t => t.status === 'completed').length;
      
      // Calculate overdue tasks
      const today = new Date();
      const overdue = allTasks.filter(t => 
        t.due_date && new Date(t.due_date) < today && t.status !== 'completed'
      ).length;

      // Calculate today's hours from timesheets
      const todayStr = new Date().toISOString().split('T')[0];
      const todayTimesheets = (timesheetsRes.data || []).filter(t => 
        t.date && t.date.startsWith(todayStr)
      );
      const totalTodayHours = todayTimesheets.reduce((sum, t) => sum + (t.hours || 0), 0);

      // Calculate client-wise task breakup
      const clientTasks = {};
      allTasks.forEach(task => {
        const clientName = task.client_name || task.assigned_by_name || 'Unassigned';
        if (!clientTasks[clientName]) {
          clientTasks[clientName] = {
            name: clientName,
            pending: 0,
            inProgress: 0,
            completed: 0,
            overdue: 0,
            totalHours: task.client_hours || 2, // Default 2 hours per client
            avatar: task.client_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${clientName}`
          };
        }
        
        if (task.status === 'pending') clientTasks[clientName].pending++;
        else if (task.status === 'in_progress') clientTasks[clientName].inProgress++;
        else if (task.status === 'completed') clientTasks[clientName].completed++;
        
        if (task.due_date && new Date(task.due_date) < today && task.status !== 'completed') {
          clientTasks[clientName].overdue++;
        }
      });

      setStats({
        pendingTasks: pending,
        inProgressTasks: inProgress,
        completedTasks: completed,
        overdueTasks: overdue,
        weeklyHours: 32 // Mock data
      });

      setMyTasks(allTasks.slice(0, 5));
      setTodayHours(totalTodayHours);
      setClientBreakup(Object.values(clientTasks));
      setAttendanceStatus(attendanceRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
    <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  const statsArray = [
    {
      title: 'Pending Tasks',
      value: stats?.pendingTasks || 0,
      icon: AlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'In Progress',
      value: stats?.inProgressTasks || 0,
      icon: Clock,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Completed',
      value: stats?.completedTasks || 0,
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Overdue',
      value: stats?.overdueTasks || 0,
      icon: AlertCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Hello, {user.name || 'VA'}!
          </h1>
          <p className="text-[#4B5563] mt-1 text-sm">
            Here's your work overview for today
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/attendance">
            <Button variant="outline" data-testid="attendance-btn">
              <LogIn className="w-4 h-4 mr-2" />
              {attendanceStatus?.clocked_in ? 'View Attendance' : 'Clock In'}
            </Button>
          </Link>
          <Link to="/va-reports">
            <Button className="bg-[#5FD068] hover:bg-[#4ec057] text-white" data-testid="submit-eod-btn">
              <FileText className="w-4 h-4 mr-2" />
              Submit EOD
            </Button>
          </Link>
        </div>
      </div>

      {/* Attendance Alert */}
      {!attendanceStatus?.clocked_in && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                <span className="text-orange-700">
                  {attendanceStatus?.clock_out ? 'Session complete. Start a new one?' : "You haven't clocked in today"}
                </span>
              </div>
              <Link to="/attendance">
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                  {attendanceStatus?.clock_out ? 'New Session' : 'Clock In Now'}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statsArray.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow" data-testid={`va-stat-card-${index}`}>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-xl sm:text-2xl font-medium tracking-tight text-[#0A0D0A] mt-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{stat.value}</p>
                  </div>
                  <div className={`${stat.bgColor} p-2 sm:p-3 rounded-lg`}>
                    <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* My Assigned Clients */}
      <Card data-testid="my-clients-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-500" />
            My Clients ({myClients.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {myClients.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {myClients.map((client, i) => (
                <div key={client.id || i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`my-client-${i}`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {(client.name || '?').charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{client.name || client.email}</p>
                    <p className="text-xs text-gray-500 truncate">{client.email}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No clients assigned yet</p>
              <p className="text-sm text-gray-400 mt-1">Clients will appear here once assigned to you</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client-wise Task Breakup */}
      {clientBreakup.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-500" />
              Client-wise Task Breakup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {clientBreakup.map((client, index) => {
                const totalTasks = client.pending + client.inProgress + client.completed;
                const completionRate = totalTasks > 0 ? Math.round((client.completed / totalTasks) * 100) : 0;
                
                return (
    <div key={index} className="p-4 bg-gray-50 rounded-lg" data-testid={`client-breakup-${index}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <img 
                          src={client.avatar} 
                          alt={client.name} 
                          className="w-10 h-10 rounded-full"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">{client.name}</h4>
                          <p className="text-sm text-gray-500">{client.totalHours}h/day allocated</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{totalTasks}</p>
                        <p className="text-xs text-gray-500">Total Tasks</p>
                      </div>
                    </div>
                    
                    {/* Task Status Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      <Badge className="bg-orange-100 text-orange-700">
                        Pending: {client.pending}
                      </Badge>
                      <Badge className="bg-blue-100 text-blue-700">
                        In Progress: {client.inProgress}
                      </Badge>
                      <Badge className="bg-green-100 text-green-700">
                        Done: {client.completed}
                      </Badge>
                      {client.overdue > 0 && (
                        <Badge className="bg-red-100 text-red-700">
                          Overdue: {client.overdue}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Completion</span>
                        <span className="font-medium text-gray-900">{completionRate}%</span>
                      </div>
                      <Progress value={completionRate} className="h-2" />
                    </div>
                  </div>
    );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="w-5 h-5" />
            Today's Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Hours Logged Today</span>
              <span className="text-2xl font-medium tracking-tight text-[#0A0D0A]" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{todayHours}h</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-[#5FD068] h-3 rounded-full transition-all duration-500"
                style={{ width: `${Math.min((todayHours / 9) * 100, 100)}%` }}
              />
            </div>
            <p className="text-sm text-gray-500">
              {todayHours >= 9 
                ? "Great job! You've completed your daily hours." 
                : `${(9 - todayHours).toFixed(1)} hours remaining to complete your daily target`}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link to="/tasks">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center h-32">
              <ClipboardList className="w-8 h-8 text-blue-500 mb-2" />
              <span className="font-medium text-gray-900">My Tasks</span>
              <span className="text-xs text-gray-500 mt-1">View all tasks</span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/attendance">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center h-32">
              <LogIn className="w-8 h-8 text-purple-500 mb-2" />
              <span className="font-medium text-gray-900">Attendance</span>
              <span className="text-xs text-gray-500 mt-1">Clock in/out</span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/leave">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center h-32">
              <Calendar className="w-8 h-8 text-orange-500 mb-2" />
              <span className="font-medium text-gray-900">Leave</span>
              <span className="text-xs text-gray-500 mt-1">Request time off</span>
            </CardContent>
          </Card>
        </Link>
        <Link to="/assessments">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-4 flex flex-col items-center justify-center text-center h-32">
              <Award className="w-8 h-8 text-green-500 mb-2" />
              <span className="font-medium text-gray-900">Skills</span>
              <span className="text-xs text-gray-500 mt-1">Take assessments</span>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* My Tasks */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>My Tasks</CardTitle>
          <Link to="/tasks" className="text-sm text-blue-500 hover:text-blue-600">
            View All
          </Link>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {myTasks.length > 0 ? myTasks.map((task) => (
              <Link to={`/tasks?taskId=${task.id}`} key={task.id}>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900 truncate">{task.title}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 truncate">{task.description}</p>
                    {task.due_date && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Due: {formatDateInTimezone(task.due_date, getUserTimezone(), 'medium')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${
                      task.status === 'completed' ? 'bg-green-100 text-green-700' :
                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {task.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </Link>
            )) : (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No tasks assigned yet</p>
                <p className="text-sm text-gray-400 mt-1">Tasks assigned to you will appear here</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Performance Insights */}
      <Card className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border-none">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-white p-3 rounded-full shadow-sm">
                <TrendingUp className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Your Performance</h3>
                <p className="text-sm text-[#4B5563] mt-1 text-sm">
                  {stats?.completedTasks > 0 
                    ? `You've completed ${stats.completedTasks} task${stats.completedTasks !== 1 ? 's' : ''}. ${stats.pendingTasks > 0 ? `${stats.pendingTasks} more pending.` : 'Great job!'}`
                    : 'Complete your first task to start tracking performance.'}
                </p>
              </div>
            </div>
            <Link to="/performance">
              <Button variant="outline" data-testid="view-performance-btn">
                View Details
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VADashboard;
